import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Sparkles, Save, Cloud, Download, Upload } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useState, useEffect } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CVForm = ({ cvData, setCvData }) => {
  const [generatingSummary, setGeneratingSummary] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [loadingDraft, setLoadingDraft] = useState(false);
  const [loadEmail, setLoadEmail] = useState("");
  const [loadCode, setLoadCode] = useState("");
  const [showLoadInput, setShowLoadInput] = useState(false);
  const [lastAccessCode, setLastAccessCode] = useState("");
  
  // Multiple versions state
  const [currentVersionId, setCurrentVersionId] = useState(null);
  const [currentVersionName, setCurrentVersionName] = useState("");
  const [savedVersions, setSavedVersions] = useState([]);
  const [showVersions, setShowVersions] = useState(false);
  const [newVersionName, setNewVersionName] = useState("");
  const [showNewVersionInput, setShowNewVersionInput] = useState(false);

  // Load saved draft from localStorage on mount
  useEffect(() => {
    const savedDraft = localStorage.getItem('cvforge_draft');
    const savedVersionId = localStorage.getItem('cvforge_version_id');
    const savedVersionName = localStorage.getItem('cvforge_version_name');
    
    if (savedDraft) {
      try {
        const parsed = JSON.parse(savedDraft);
        if (!cvData.fullName && parsed.fullName) {
          setCvData(parsed);
          if (savedVersionId) setCurrentVersionId(savedVersionId);
          if (savedVersionName) setCurrentVersionName(savedVersionName);
          toast.info("Previous local draft loaded");
        }
      } catch (e) {
        console.error("Failed to load draft", e);
      }
    }
  }, []);

  const handleChange = (field, value) => {
    setCvData({ ...cvData, [field]: value });
  };

  const handleWorkChange = (index, field, value) => {
    const updated = [...cvData.workExperience];
    updated[index][field] = value;
    setCvData({ ...cvData, workExperience: updated });
  };

  const addWork = () => {
    setCvData({
      ...cvData,
      workExperience: [...cvData.workExperience, { role: "", company: "", startDate: "", endDate: "", description: "" }]
    });
  };

  const removeWork = (index) => {
    const updated = cvData.workExperience.filter((_, i) => i !== index);
    setCvData({ ...cvData, workExperience: updated });
  };

  const handleEduChange = (index, field, value) => {
    const updated = [...cvData.education];
    updated[index][field] = value;
    setCvData({ ...cvData, education: updated });
  };

  const addEducation = () => {
    setCvData({
      ...cvData,
      education: [...cvData.education, { degree: "", institution: "", graduationYear: "", subjects: "" }]
    });
  };

  const removeEducation = (index) => {
    const updated = cvData.education.filter((_, i) => i !== index);
    setCvData({ ...cvData, education: updated });
  };

  const handleSkillChange = (index, field, value) => {
    const updated = [...(cvData.skills || [])];
    updated[index][field] = value;
    setCvData({ ...cvData, skills: updated });
  };

  const addSkill = () => {
    setCvData({
      ...cvData,
      skills: [...(cvData.skills || []), { name: "", level: "Intermediate" }]
    });
  };

  const removeSkill = (index) => {
    const updated = (cvData.skills || []).filter((_, i) => i !== index);
    setCvData({ ...cvData, skills: updated });
  };

  const handleLanguageChange = (index, field, value) => {
    const updated = [...(cvData.languages || [])];
    updated[index][field] = value;
    setCvData({ ...cvData, languages: updated });
  };

  const addLanguage = () => {
    setCvData({
      ...cvData,
      languages: [...(cvData.languages || []), { name: "", proficiency: "Professional" }]
    });
  };

  const removeLanguage = (index) => {
    const updated = (cvData.languages || []).filter((_, i) => i !== index);
    setCvData({ ...cvData, languages: updated });
  };

  const generateSummary = async () => {
    if (!cvData.workExperience || cvData.workExperience.length === 0 || !cvData.workExperience[0]?.role) {
      toast.error("Please add at least one work experience first");
      return;
    }

    setGeneratingSummary(true);
    try {
      const validRoles = cvData.workExperience.filter(w => w.role && w.company);

      // Identify the most recent role: prefer one with endDate "Present" (case-insensitive),
      // otherwise the one with the latest end date, else fall back to first entry.
      const parseDate = (s) => {
        if (!s) return 0;
        const t = Date.parse(s);
        if (!Number.isNaN(t)) return t;
        const m = String(s).match(/\d{4}/);
        return m ? Date.parse(`${m[0]}-01-01`) : 0;
      };
      const presentIdx = validRoles.findIndex(
        (w) => w.endDate && /present|current|now/i.test(w.endDate)
      );
      let mostRecent = validRoles[0];
      if (presentIdx >= 0) {
        mostRecent = validRoles[presentIdx];
      } else if (validRoles.length > 1) {
        mostRecent = [...validRoles].sort(
          (a, b) => parseDate(b.endDate) - parseDate(a.endDate)
        )[0];
      }

      // Compute total years of experience from the earliest start to the latest end (or today)
      let totalYears = "";
      const starts = validRoles.map((w) => parseDate(w.startDate)).filter(Boolean);
      const ends = validRoles.map((w) =>
        /present|current|now/i.test(w.endDate || "")
          ? Date.now()
          : parseDate(w.endDate)
      ).filter(Boolean);
      if (starts.length && ends.length) {
        const years = Math.max(1, Math.round((Math.max(...ends) - Math.min(...starts)) / (365.25 * 24 * 3600 * 1000)));
        totalYears = `${years}+ years of experience across ${validRoles.length} role${validRoles.length > 1 ? "s" : ""}`;
      } else {
        totalYears = `${validRoles.length}+ role${validRoles.length > 1 ? "s" : ""}`;
      }

      // Build a detailed, per-role summary (dates included, empty descriptions skipped)
      const workSummary = validRoles
        .map((w, i) => {
          const dates =
            w.startDate || w.endDate ? ` (${w.startDate || "?"} – ${w.endDate || "?"})` : "";
          const desc = (w.description || "").trim();
          const descPart = desc ? ` — ${desc}` : "";
          return `${i + 1}. ${w.role} at ${w.company}${dates}${descPart}`;
        })
        .join("\n");

      const skills = (cvData.skills || []).map((s) => s.name).filter(Boolean).join(", ");

      const response = await axios.post(`${API}/generate-summary`, {
        role: mostRecent.role,
        company: mostRecent.company,
        yearsOfExperience: totalYears,
        allExperience: workSummary,
        skills: skills,
      });

      setCvData({ ...cvData, summary: response.data.summary });
      toast.success("Professional summary generated from your experience!");
    } catch (error) {
      toast.error("Failed to generate summary");
      console.error(error);
    } finally {
      setGeneratingSummary(false);
    }
  };

  const saveDraft = async (asNewVersion = false) => {
    if (!cvData.email || !cvData.email.includes('@')) {
      // If no email, just save locally
      try {
        localStorage.setItem('cvforge_draft', JSON.stringify(cvData));
        toast.success("Draft saved locally! Add your email to save to cloud.");
      } catch (e) {
        toast.error("Failed to save draft");
      }
      return;
    }

    setSavingDraft(true);
    try {
      // Save to localStorage
      localStorage.setItem('cvforge_draft', JSON.stringify(cvData));
      
      const versionName = asNewVersion 
        ? (newVersionName || cvData.fullName || 'My CV')
        : (currentVersionName || cvData.fullName || 'My CV');
      
      const response = await axios.post(`${API}/draft/save`, {
        email: cvData.email,
        cvData: cvData,
        versionName: versionName,
        versionId: asNewVersion ? null : currentVersionId
      });
      
      const accessCode = response.data.accessCode;
      const versionId = response.data.versionId;
      const savedVersionName = response.data.versionName;
      
      setLastAccessCode(accessCode);
      setCurrentVersionId(versionId);
      setCurrentVersionName(savedVersionName);
      setShowNewVersionInput(false);
      setNewVersionName("");
      
      // Save version info to localStorage
      localStorage.setItem('cvforge_version_id', versionId);
      localStorage.setItem('cvforge_version_name', savedVersionName);
      
      toast.success(
        <div>
          <p><strong>{asNewVersion ? 'New version created!' : 'Draft saved!'}</strong></p>
          <p className="text-sm mt-1">Version: <strong>{savedVersionName}</strong></p>
          <p className="text-sm mt-1">Access code: <strong className="font-mono">{accessCode}</strong></p>
        </div>,
        { duration: 10000 }
      );
    } catch (e) {
      toast.info("Saved locally. Cloud save failed - try again later.");
    } finally {
      setSavingDraft(false);
    }
  };

  const loadVersionsList = async () => {
    if (!loadEmail || !loadCode) {
      toast.error("Please enter email and access code first");
      return;
    }
    
    try {
      const response = await axios.post(`${API}/draft/list`, {
        email: loadEmail,
        accessCode: loadCode
      });
      
      if (response.data.versions) {
        setSavedVersions(response.data.versions);
        setShowVersions(true);
      }
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error("Invalid email or access code");
      } else {
        toast.error("Failed to load versions");
      }
    }
  };

  const loadSpecificVersion = async (versionId) => {
    setLoadingDraft(true);
    try {
      const response = await axios.post(`${API}/draft/load`, {
        email: loadEmail,
        accessCode: loadCode,
        versionId: versionId
      });
      
      if (response.data.cvData) {
        setCvData(response.data.cvData);
        setCurrentVersionId(response.data.versionId);
        setCurrentVersionName(response.data.versionName);
        
        localStorage.setItem('cvforge_draft', JSON.stringify(response.data.cvData));
        localStorage.setItem('cvforge_version_id', response.data.versionId);
        localStorage.setItem('cvforge_version_name', response.data.versionName);
        
        toast.success(`Loaded: ${response.data.versionName}`);
        setShowLoadInput(false);
        setShowVersions(false);
        setLoadEmail("");
        setLoadCode("");
      }
    } catch (error) {
      toast.error("Failed to load version");
    } finally {
      setLoadingDraft(false);
    }
  };

  const deleteVersion = async (versionId, versionName) => {
    if (!window.confirm(`Delete "${versionName}"? This cannot be undone.`)) return;
    
    try {
      await axios.delete(`${API}/draft/${versionId}?email=${encodeURIComponent(loadEmail)}&accessCode=${loadCode}`);
      toast.success("Version deleted");
      // Refresh the list
      loadVersionsList();
    } catch (error) {
      toast.error("Failed to delete version");
    }
  };

  const loadDraft = async () => {
    if (!loadEmail || !loadEmail.includes('@')) {
      toast.error("Please enter a valid email address");
      return;
    }
    if (!loadCode || loadCode.length < 6) {
      toast.error("Please enter your 6-digit access code");
      return;
    }

    // First load the versions list
    await loadVersionsList();
  };

  const clearDraft = () => {
    localStorage.removeItem('cvforge_draft');
    localStorage.removeItem('cvforge_version_id');
    localStorage.removeItem('cvforge_version_name');
    setCurrentVersionId(null);
    setCurrentVersionName("");
    toast.info("Local draft cleared");
  };

  const startNewVersion = () => {
    setShowNewVersionInput(true);
    setNewVersionName(cvData.fullName ? `${cvData.fullName} - Copy` : "New CV");
  };

  return (
    <div className="space-y-8">
      {/* Current Version Indicator */}
      {currentVersionName && (
        <div className="bg-accent/30 border border-accent px-3 py-2 flex items-center justify-between">
          <span className="text-sm">
            Editing: <strong>{currentVersionName}</strong>
          </span>
          <Button
            onClick={startNewVersion}
            variant="ghost"
            size="sm"
            className="text-xs h-7"
          >
            + New Version
          </Button>
        </div>
      )}
      
      {/* Save/Load Progress - Fixed at top */}
      <div className="sticky top-0 bg-background py-3 z-10 border-b border-border space-y-2">
        <div className="flex justify-between items-center gap-2">
          <div className="flex gap-2">
            <Button
              onClick={() => { setShowLoadInput(!showLoadInput); setShowVersions(false); }}
              variant="outline"
              size="sm"
              className="rounded-none"
            >
              <Download className="h-4 w-4 mr-1" />
              Load Draft
            </Button>
            <Button
              onClick={clearDraft}
              variant="ghost"
              size="sm"
              className="text-muted-foreground hover:text-destructive rounded-none"
            >
              Clear
            </Button>
          </div>
          <Button
            onClick={() => saveDraft(false)}
            disabled={savingDraft}
            size="sm"
            className="rounded-none bg-accent"
          >
            <Cloud className="h-4 w-4 mr-1" />
            {savingDraft ? "Saving..." : "Save Progress"}
          </Button>
        </div>
        
        {/* New Version Input */}
        {showNewVersionInput && (
          <div className="flex gap-2 p-3 bg-accent/20 border border-accent">
            <Input
              type="text"
              placeholder="New version name"
              value={newVersionName}
              onChange={(e) => setNewVersionName(e.target.value)}
              className="flex-1 rounded-none h-9"
            />
            <Button
              onClick={() => saveDraft(true)}
              disabled={savingDraft}
              size="sm"
              className="rounded-none"
            >
              Create
            </Button>
            <Button
              onClick={() => { setShowNewVersionInput(false); setNewVersionName(""); }}
              variant="ghost"
              size="sm"
              className="rounded-none"
            >
              Cancel
            </Button>
          </div>
        )}
        
        {showLoadInput && (
          <div className="flex flex-col gap-2 p-3 bg-secondary/30 border border-border">
            <div className="flex flex-col sm:flex-row gap-2">
              <Input
                type="email"
                placeholder="Email address"
                value={loadEmail}
                onChange={(e) => setLoadEmail(e.target.value)}
                className="flex-1 rounded-none h-9"
              />
              <Input
                type="text"
                placeholder="Access code"
                value={loadCode}
                onChange={(e) => setLoadCode(e.target.value)}
                maxLength={6}
                className="w-full sm:w-32 rounded-none h-9 font-mono"
              />
            </div>
            <div className="flex gap-2">
              <Button
                onClick={loadDraft}
                disabled={loadingDraft}
                size="sm"
                className="flex-1 rounded-none"
              >
                {loadingDraft ? "Loading..." : "Load Versions"}
              </Button>
              <Button
                onClick={() => {setShowLoadInput(false); setShowVersions(false); setLoadEmail(""); setLoadCode(""); setSavedVersions([]);}}
                variant="ghost"
                size="sm"
                className="rounded-none"
              >
                Cancel
              </Button>
            </div>
            
            {/* Versions List */}
            {showVersions && savedVersions.length > 0 && (
              <div className="mt-2 space-y-2 max-h-[50vh] overflow-y-auto pr-1">
                <p className="text-xs text-muted-foreground font-medium sticky top-0 bg-secondary/30 py-1">Your saved CV versions:</p>
                {savedVersions.map((version) => (
                  <div 
                    key={version.versionId} 
                    className="flex items-center justify-between p-2 bg-background border border-border hover:bg-accent/10"
                  >
                    <div>
                      <p className="text-sm font-medium">{version.versionName}</p>
                      <p className="text-xs text-muted-foreground">
                        Updated: {new Date(version.updatedAt).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex gap-1">
                      <Button
                        onClick={() => loadSpecificVersion(version.versionId)}
                        disabled={loadingDraft}
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs rounded-none"
                      >
                        Load
                      </Button>
                      <Button
                        onClick={() => deleteVersion(version.versionId, version.versionName)}
                        size="sm"
                        variant="ghost"
                        className="h-7 text-xs rounded-none text-destructive hover:bg-destructive/10"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {showVersions && savedVersions.length === 0 && (
              <p className="text-xs text-muted-foreground text-center py-2">No saved versions found.</p>
            )}
            
            <p className="text-xs text-muted-foreground">
              Enter the email and 6-digit access code you received when saving.
            </p>
          </div>
        )}
        
        <p className="text-xs text-muted-foreground">
          💡 Click "Save Progress" to save your current CV. Use "+ New Version" to create multiple CV variations.
        </p>
      </div>

      {/* Template Selection */}
      <section>
        <h2 className="font-heading text-xl font-medium mb-6 pb-2 border-b border-border">Template Style</h2>
        <div className="space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Choose Template</Label>
            <Select 
              value={cvData.template || "professional"} 
              onValueChange={(value) => handleChange("template", value)}
            >
              <SelectTrigger data-testid="template-selector" className="bg-card border border-input rounded-none h-10">
                <SelectValue placeholder="Select template" />
              </SelectTrigger>
              <SelectContent className="bg-card border border-border">
                <SelectItem value="professional">Professional (Black & White)</SelectItem>
                <SelectItem value="modern">Modern (Green Accent)</SelectItem>
                <SelectItem value="creative">Creative (Purple & Pink)</SelectItem>
                <SelectItem value="executive">Executive (Navy & Gold)</SelectItem>
                <SelectItem value="minimalist">Minimalist (Ultra Clean)</SelectItem>
                <SelectItem value="academic">Academic (Classic Scholarly)</SelectItem>
                <SelectItem value="technical">Technical (Developer Focused)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <Label className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Choose Font</Label>
            <Select 
              value={cvData.font || "helvetica"} 
              onValueChange={(value) => handleChange("font", value)}
            >
              <SelectTrigger data-testid="font-selector" className="bg-card border border-input rounded-none h-10">
                <SelectValue placeholder="Select font" />
              </SelectTrigger>
              <SelectContent className="bg-card border border-border">
                <SelectItem value="helvetica">Helvetica (Modern Sans-Serif)</SelectItem>
                <SelectItem value="georgia">Georgia (Elegant Serif)</SelectItem>
                <SelectItem value="arial">Arial (Clean & Universal)</SelectItem>
                <SelectItem value="times">Times New Roman (Classic)</SelectItem>
                <SelectItem value="palatino">Palatino (Professional Serif)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {/* Color Scheme Selection */}
          <div>
            <Label className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Color Scheme</Label>
            <Select 
              value={cvData.colorScheme || "default"} 
              onValueChange={(value) => handleChange("colorScheme", value)}
            >
              <SelectTrigger data-testid="color-scheme-selector" className="bg-card border border-input rounded-none h-10">
                <SelectValue placeholder="Select color scheme" />
              </SelectTrigger>
              <SelectContent className="bg-card border border-border">
                <SelectItem value="default">Default (Template Colors)</SelectItem>
                <SelectItem value="ocean">Ocean Blue (#0077B6)</SelectItem>
                <SelectItem value="forest">Forest Green (#2D6A4F)</SelectItem>
                <SelectItem value="burgundy">Burgundy Red (#800020)</SelectItem>
                <SelectItem value="slate">Slate Gray (#475569)</SelectItem>
                <SelectItem value="royal">Royal Purple (#7B2D8E)</SelectItem>
                <SelectItem value="copper">Copper Bronze (#B87333)</SelectItem>
                <SelectItem value="midnight">Midnight Black (#1A1A2E)</SelectItem>
                <SelectItem value="custom">Custom Color</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {/* Custom Color Picker - shown only when "custom" is selected */}
          {cvData.colorScheme === "custom" && (
            <div className="space-y-3">
              <div>
                <Label className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Primary Color</Label>
                <div className="flex gap-2 items-center">
                  <Input
                    type="color"
                    value={cvData.customPrimaryColor || "#18181B"}
                    onChange={(e) => handleChange("customPrimaryColor", e.target.value)}
                    className="w-12 h-10 p-1 rounded-none border border-input cursor-pointer"
                    data-testid="custom-primary-color"
                  />
                  <Input
                    type="text"
                    value={cvData.customPrimaryColor || "#18181B"}
                    onChange={(e) => handleChange("customPrimaryColor", e.target.value)}
                    placeholder="#18181B"
                    className="flex-1 bg-transparent border border-input rounded-none h-10"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Accent Color</Label>
                <div className="flex gap-2 items-center">
                  <Input
                    type="color"
                    value={cvData.customAccentColor || "#F4F4F5"}
                    onChange={(e) => handleChange("customAccentColor", e.target.value)}
                    className="w-12 h-10 p-1 rounded-none border border-input cursor-pointer"
                    data-testid="custom-accent-color"
                  />
                  <Input
                    type="text"
                    value={cvData.customAccentColor || "#F4F4F5"}
                    onChange={(e) => handleChange("customAccentColor", e.target.value)}
                    placeholder="#F4F4F5"
                    className="flex-1 bg-transparent border border-input rounded-none h-10"
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Photo Upload */}
      <section>
        <h2 className="font-heading text-xl font-medium mb-6 pb-2 border-b border-border">Profile Photo (Optional)</h2>
        <div className="space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Upload Photo</Label>
            <input
              id="photo"
              data-testid="photo-upload"
              type="file"
              accept="image/*"
              onChange={(e) => {
                const file = e.target.files[0];
                if (!file) return;
                if (file.size > 15000000) {
                  toast.error("Image is too large. Please use an image under 15MB.");
                  return;
                }
                // Compress + resize to 400x400 square (centered crop) to avoid mobile crashes
                const reader = new FileReader();
                reader.onload = (ev) => {
                  const img = new Image();
                  img.onload = () => {
                    try {
                      const SIZE = 400;
                      const canvas = document.createElement("canvas");
                      canvas.width = SIZE;
                      canvas.height = SIZE;
                      const ctx = canvas.getContext("2d");
                      // Center-crop to square
                      const minSide = Math.min(img.width, img.height);
                      const sx = (img.width - minSide) / 2;
                      const sy = (img.height - minSide) / 2;
                      ctx.drawImage(img, sx, sy, minSide, minSide, 0, 0, SIZE, SIZE);
                      const compressed = canvas.toDataURL("image/jpeg", 0.85);
                      handleChange("photo", compressed);
                      toast.success("Photo uploaded!");
                    } catch (err) {
                      toast.error("Could not process photo. Try a different image.");
                    }
                  };
                  img.onerror = () => toast.error("Could not read image file.");
                  img.src = ev.target.result;
                };
                reader.onerror = () => toast.error("Failed to read file.");
                reader.readAsDataURL(file);
                // Allow re-uploading the same file
                e.target.value = "";
              }}
              className="hidden"
            />
            <label 
              htmlFor="photo"
              className="flex items-center justify-center gap-2 w-full h-12 bg-secondary/30 border border-input rounded-none cursor-pointer hover:bg-secondary/50 transition-colors"
            >
              <Upload className="w-4 h-4" />
              <span className="text-sm font-medium">Add Photo or Browse</span>
            </label>
            <p className="text-xs text-muted-foreground mt-1">Max size: 5MB. Recommended: Square photo.</p>
          </div>
          {cvData.photo && (
            <div className="flex items-center gap-4">
              <img 
                src={cvData.photo} 
                alt="Profile preview" 
                className="w-20 h-20 rounded-full object-cover border-2 border-border"
              />
              <Button
                onClick={() => handleChange("photo", "")}
                variant="ghost"
                size="sm"
                className="text-destructive hover:bg-destructive/10"
              >
                Remove Photo
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* Personal Details */}
      <section>
        <h2 className="font-heading text-xl font-medium mb-6 pb-2 border-b border-border">Personal Details</h2>
        <div className="space-y-4">
          <div>
            <Label htmlFor="fullName" className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Full Name</Label>
            <Input
              id="fullName"
              data-testid="personal-name"
              value={cvData.fullName}
              onChange={(e) => handleChange("fullName", e.target.value)}
              className="bg-transparent border-b border-input rounded-none px-0 py-2 focus-visible:ring-0 focus-visible:border-primary transition-colors"
              placeholder="Enter your full name"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Email</Label>
              <Input
                id="email"
                data-testid="personal-email"
                type="email"
                value={cvData.email}
                onChange={(e) => handleChange("email", e.target.value)}
                className="bg-transparent border-b border-input rounded-none px-0 py-2 focus-visible:ring-0 focus-visible:border-primary transition-colors"
                placeholder="your.email@example.com"
              />
            </div>
            <div>
              <Label htmlFor="phone" className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Phone</Label>
              <Input
                id="phone"
                data-testid="personal-phone"
                value={cvData.phone}
                onChange={(e) => handleChange("phone", e.target.value)}
                className="bg-transparent border-b border-input rounded-none px-0 py-2 focus-visible:ring-0 focus-visible:border-primary transition-colors"
                placeholder="Your phone number"
              />
            </div>
          </div>
          
          {/* Address Fields */}
          <div>
            <Label className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Street Address</Label>
            <Input
              data-testid="personal-street"
              value={cvData.streetAddress || ""}
              onChange={(e) => handleChange("streetAddress", e.target.value)}
              className="bg-transparent border-b border-input rounded-none px-0 py-2 focus-visible:ring-0 focus-visible:border-primary transition-colors"
              placeholder="123 Main Street"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-widest text-muted-foreground mb-1">City</Label>
              <Input
                data-testid="personal-city"
                value={cvData.city || ""}
                onChange={(e) => handleChange("city", e.target.value)}
                className="bg-transparent border-b border-input rounded-none px-0 py-2 focus-visible:ring-0 focus-visible:border-primary transition-colors"
                placeholder="City"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-muted-foreground mb-1">State/Province</Label>
              <Input
                data-testid="personal-state"
                value={cvData.state || ""}
                onChange={(e) => handleChange("state", e.target.value)}
                className="bg-transparent border-b border-input rounded-none px-0 py-2 focus-visible:ring-0 focus-visible:border-primary transition-colors"
                placeholder="State/Province"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Postal Code</Label>
              <Input
                data-testid="personal-postal"
                value={cvData.postalCode || ""}
                onChange={(e) => handleChange("postalCode", e.target.value)}
                className="bg-transparent border-b border-input rounded-none px-0 py-2 focus-visible:ring-0 focus-visible:border-primary transition-colors"
                placeholder="Postal Code"
              />
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Country</Label>
            <Input
              data-testid="personal-country"
              value={cvData.country || ""}
              onChange={(e) => handleChange("country", e.target.value)}
              className="bg-transparent border-b border-input rounded-none px-0 py-2 focus-visible:ring-0 focus-visible:border-primary transition-colors"
              placeholder="Country"
            />
          </div>

          {/* Additional Personal Details */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Date of Birth (Optional)</Label>
              <Input
                data-testid="personal-dob"
                type="date"
                value={cvData.dateOfBirth || ""}
                onChange={(e) => handleChange("dateOfBirth", e.target.value)}
                className="bg-transparent border-b border-input rounded-none px-0 py-2 focus-visible:ring-0 focus-visible:border-primary transition-colors"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Nationality (Optional)</Label>
              <Input
                data-testid="personal-nationality"
                value={cvData.nationality || ""}
                onChange={(e) => handleChange("nationality", e.target.value)}
                className="bg-transparent border-b border-input rounded-none px-0 py-2 focus-visible:ring-0 focus-visible:border-primary transition-colors"
                placeholder="Your nationality"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-widest text-muted-foreground mb-1">ID / Passport Number (Optional)</Label>
              <Input
                data-testid="personal-id"
                value={cvData.idNumber || ""}
                onChange={(e) => handleChange("idNumber", e.target.value)}
                className="bg-transparent border-b border-input rounded-none px-0 py-2 focus-visible:ring-0 focus-visible:border-primary transition-colors"
                placeholder="Identification number"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Languages (Optional)</Label>
              <Input
                data-testid="personal-languages"
                value={cvData.languagesSpoken || ""}
                onChange={(e) => handleChange("languagesSpoken", e.target.value)}
                className="bg-transparent border-b border-input rounded-none px-0 py-2 focus-visible:ring-0 focus-visible:border-primary transition-colors"
                placeholder="e.g. English, French, Spanish"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-widest text-muted-foreground mb-1">LinkedIn (Optional)</Label>
              <Input
                data-testid="personal-linkedin"
                value={cvData.linkedin || ""}
                onChange={(e) => handleChange("linkedin", e.target.value)}
                className="bg-transparent border-b border-input rounded-none px-0 py-2 focus-visible:ring-0 focus-visible:border-primary transition-colors"
                placeholder="linkedin.com/in/yourprofile"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Website (Optional)</Label>
              <Input
                data-testid="personal-website"
                value={cvData.website || ""}
                onChange={(e) => handleChange("website", e.target.value)}
                className="bg-transparent border-b border-input rounded-none px-0 py-2 focus-visible:ring-0 focus-visible:border-primary transition-colors"
                placeholder="www.yourwebsite.com"
              />
            </div>
          </div>

          {/* Professional Summary */}
          <div>
            <div className="flex justify-between items-center mb-1">
              <Label htmlFor="summary" className="text-xs uppercase tracking-widest text-muted-foreground">Professional Summary</Label>
              <Button
                data-testid="generate-summary-btn"
                onClick={generateSummary}
                disabled={generatingSummary}
                variant="ghost"
                size="sm"
                className="hover:bg-accent/10 hover:text-accent rounded-none h-8"
              >
                <Sparkles className="h-3 w-3 mr-1" />
                {generatingSummary ? "Generating..." : "Generate from Experience"}
              </Button>
            </div>
            <Textarea
              id="summary"
              data-testid="personal-summary"
              value={cvData.summary}
              onChange={(e) => handleChange("summary", e.target.value)}
              className="bg-transparent border border-input rounded-none px-3 py-2 focus-visible:ring-0 focus-visible:border-primary transition-colors min-h-[100px]"
              placeholder="A brief professional summary highlighting your key strengths and career objectives..."
            />
            <p className="text-xs text-muted-foreground mt-1">Tip: Add your work experience first, then click "Generate from Experience" to auto-create a summary.</p>
          </div>
        </div>
      </section>

      {/* Work Experience */}
      <section>
        <div className="flex justify-between items-center mb-6 pb-2 border-b border-border">
          <h2 className="font-heading text-xl font-medium">Work Experience</h2>
          <Button
            data-testid="add-work-btn"
            onClick={addWork}
            variant="ghost"
            size="sm"
            className="hover:bg-accent/10 hover:text-accent rounded-none"
          >
            <Plus className="h-4 w-4 mr-1" />
            Add
          </Button>
        </div>
        <div className="space-y-6">
          {cvData.workExperience.map((work, index) => (
            <div key={index} className="space-y-4 p-4 border border-border/40 rounded-none relative">
              {cvData.workExperience.length > 1 && (
                <Button
                  data-testid={`remove-work-${index}`}
                  onClick={() => removeWork(index)}
                  variant="ghost"
                  size="sm"
                  className="absolute top-2 right-2 hover:bg-destructive/10 hover:text-destructive rounded-none"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
              <div>
                <Label className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Job Title / Role</Label>
                <Input
                  data-testid={`work-role-${index}`}
                  value={work.role}
                  onChange={(e) => handleWorkChange(index, "role", e.target.value)}
                  className="bg-transparent border-b border-input rounded-none px-0 py-2 focus-visible:ring-0 focus-visible:border-primary transition-colors"
                  placeholder="e.g. Software Developer, Marketing Manager"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Company / Organization</Label>
                <Input
                  data-testid={`work-company-${index}`}
                  value={work.company}
                  onChange={(e) => handleWorkChange(index, "company", e.target.value)}
                  className="bg-transparent border-b border-input rounded-none px-0 py-2 focus-visible:ring-0 focus-visible:border-primary transition-colors"
                  placeholder="Company name"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Start Date</Label>
                  <Input
                    data-testid={`work-start-${index}`}
                    value={work.startDate}
                    onChange={(e) => handleWorkChange(index, "startDate", e.target.value)}
                    className="bg-transparent border-b border-input rounded-none px-0 py-2 focus-visible:ring-0 focus-visible:border-primary transition-colors"
                    placeholder="e.g. January 2020"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-muted-foreground mb-1">End Date</Label>
                  <Input
                    data-testid={`work-end-${index}`}
                    value={work.endDate}
                    onChange={(e) => handleWorkChange(index, "endDate", e.target.value)}
                    className="bg-transparent border-b border-input rounded-none px-0 py-2 focus-visible:ring-0 focus-visible:border-primary transition-colors"
                    placeholder="e.g. Present or December 2023"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Key Responsibilities & Achievements</Label>
                <Textarea
                  data-testid={`work-desc-${index}`}
                  value={work.description}
                  onChange={(e) => handleWorkChange(index, "description", e.target.value)}
                  className="bg-transparent border border-input rounded-none px-3 py-2 focus-visible:ring-0 focus-visible:border-primary transition-colors min-h-[100px]"
                  placeholder="• Led a team of 5 developers&#10;• Increased sales by 25%&#10;• Managed key client accounts"
                />
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Education */}
      <section>
        <div className="flex justify-between items-center mb-6 pb-2 border-b border-border">
          <h2 className="font-heading text-xl font-medium">Education</h2>
          <Button
            data-testid="add-education-btn"
            onClick={addEducation}
            variant="ghost"
            size="sm"
            className="hover:bg-accent/10 hover:text-accent rounded-none"
          >
            <Plus className="h-4 w-4 mr-1" />
            Add
          </Button>
        </div>
        <div className="space-y-6">
          {cvData.education.map((edu, index) => (
            <div key={index} className="space-y-4 p-4 border border-border/40 rounded-none relative">
              {cvData.education.length > 1 && (
                <Button
                  data-testid={`remove-education-${index}`}
                  onClick={() => removeEducation(index)}
                  variant="ghost"
                  size="sm"
                  className="absolute top-2 right-2 hover:bg-destructive/10 hover:text-destructive rounded-none"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
              <div>
                <Label className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Degree / Diploma / Certificate</Label>
                <Input
                  data-testid={`edu-degree-${index}`}
                  value={edu.degree}
                  onChange={(e) => handleEduChange(index, "degree", e.target.value)}
                  className="bg-transparent border-b border-input rounded-none px-0 py-2 focus-visible:ring-0 focus-visible:border-primary transition-colors"
                  placeholder="e.g. Bachelor of Commerce, National Diploma, Certificate in Project Management"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Institution</Label>
                <Input
                  data-testid={`edu-institution-${index}`}
                  value={edu.institution}
                  onChange={(e) => handleEduChange(index, "institution", e.target.value)}
                  className="bg-transparent border-b border-input rounded-none px-0 py-2 focus-visible:ring-0 focus-visible:border-primary transition-colors"
                  placeholder="Institution or University name"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Graduation Year</Label>
                  <Input
                    data-testid={`edu-year-${index}`}
                    value={edu.graduationYear}
                    onChange={(e) => handleEduChange(index, "graduationYear", e.target.value)}
                    className="bg-transparent border-b border-input rounded-none px-0 py-2 focus-visible:ring-0 focus-visible:border-primary transition-colors"
                    placeholder="e.g. 2020"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Subjects (Optional)</Label>
                  <Input
                    data-testid={`edu-subjects-${index}`}
                    value={edu.subjects || ""}
                    onChange={(e) => handleEduChange(index, "subjects", e.target.value)}
                    className="bg-transparent border-b border-input rounded-none px-0 py-2 focus-visible:ring-0 focus-visible:border-primary transition-colors"
                    placeholder="e.g. Mathematics, Economics, Accounting"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Skills */}
      <section>
        <div className="flex justify-between items-center mb-6 pb-2 border-b border-border">
          <h2 className="font-heading text-xl font-medium">Skills</h2>
          <Button
            data-testid="add-skill-btn"
            onClick={addSkill}
            variant="ghost"
            size="sm"
            className="hover:bg-accent/10 hover:text-accent rounded-none"
          >
            <Plus className="h-4 w-4 mr-1" />
            Add
          </Button>
        </div>
        <div className="space-y-4">
          {(cvData.skills || []).map((skill, index) => (
            <div key={index} className="flex gap-4 items-start p-4 border border-border/40 rounded-none relative">
              <div className="flex-1">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Skill Name</Label>
                <Input
                  data-testid={`skill-name-${index}`}
                  value={skill.name}
                  onChange={(e) => handleSkillChange(index, "name", e.target.value)}
                  className="bg-transparent border-b border-input rounded-none px-0 py-2 focus-visible:ring-0 focus-visible:border-primary transition-colors"
                  placeholder="e.g. Microsoft Excel, Project Management"
                />
              </div>
              <div className="w-40">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Level</Label>
                <Select 
                  value={skill.level || "Intermediate"} 
                  onValueChange={(value) => handleSkillChange(index, "level", value)}
                >
                  <SelectTrigger className="bg-card border border-input rounded-none h-10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-card border border-border">
                    <SelectItem value="Beginner">Beginner</SelectItem>
                    <SelectItem value="Intermediate">Intermediate</SelectItem>
                    <SelectItem value="Advanced">Advanced</SelectItem>
                    <SelectItem value="Expert">Expert</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button
                data-testid={`remove-skill-${index}`}
                onClick={() => removeSkill(index)}
                variant="ghost"
                size="sm"
                className="hover:bg-destructive/10 hover:text-destructive rounded-none mt-5"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
          {(!cvData.skills || cvData.skills.length === 0) && (
            <p className="text-sm text-muted-foreground italic">No skills added yet. Click "Add" to include your skills.</p>
          )}
        </div>
      </section>

      {/* Languages */}
      <section>
        <div className="flex justify-between items-center mb-6 pb-2 border-b border-border">
          <h2 className="font-heading text-xl font-medium">Languages</h2>
          <Button
            data-testid="add-language-btn"
            onClick={addLanguage}
            variant="ghost"
            size="sm"
            className="hover:bg-accent/10 hover:text-accent rounded-none"
          >
            <Plus className="h-4 w-4 mr-1" />
            Add
          </Button>
        </div>
        <div className="space-y-4">
          {(cvData.languages || []).map((lang, index) => (
            <div key={index} className="flex gap-4 items-start p-4 border border-border/40 rounded-none relative">
              <div className="flex-1">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Language</Label>
                <Input
                  data-testid={`language-name-${index}`}
                  value={lang.name}
                  onChange={(e) => handleLanguageChange(index, "name", e.target.value)}
                  className="bg-transparent border-b border-input rounded-none px-0 py-2 focus-visible:ring-0 focus-visible:border-primary transition-colors"
                  placeholder="e.g. English, French, Mandarin"
                />
              </div>
              <div className="w-40">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Proficiency</Label>
                <Select 
                  value={lang.proficiency || "Professional"} 
                  onValueChange={(value) => handleLanguageChange(index, "proficiency", value)}
                >
                  <SelectTrigger className="bg-card border border-input rounded-none h-10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-card border border-border">
                    <SelectItem value="Basic">Basic</SelectItem>
                    <SelectItem value="Conversational">Conversational</SelectItem>
                    <SelectItem value="Professional">Professional</SelectItem>
                    <SelectItem value="Native">Native</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button
                data-testid={`remove-language-${index}`}
                onClick={() => removeLanguage(index)}
                variant="ghost"
                size="sm"
                className="hover:bg-destructive/10 hover:text-destructive rounded-none mt-5"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
          {(!cvData.languages || cvData.languages.length === 0) && (
            <p className="text-sm text-muted-foreground italic">No languages added yet. Click "Add" to include languages you speak.</p>
          )}
        </div>
      </section>

      {/* Achievements */}
      <section>
        <div className="flex justify-between items-center mb-6 pb-2 border-b border-border">
          <h2 className="font-heading text-xl font-medium">Achievements (Optional)</h2>
          <Button
            data-testid="add-achievement-btn"
            onClick={() => setCvData({...cvData, achievements: [...(cvData.achievements || []), ""]})}
            variant="ghost"
            size="sm"
            className="hover:bg-accent/10 hover:text-accent rounded-none"
          >
            <Plus className="h-4 w-4 mr-1" />
            Add
          </Button>
        </div>
        <div className="space-y-4">
          {(cvData.achievements || []).map((achievement, index) => (
            <div key={index} className="flex gap-4 items-start p-4 border border-border/40 rounded-none relative">
              <div className="flex-1">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Achievement</Label>
                <Input
                  data-testid={`achievement-${index}`}
                  value={achievement}
                  onChange={(e) => {
                    const updated = [...(cvData.achievements || [])];
                    updated[index] = e.target.value;
                    setCvData({ ...cvData, achievements: updated });
                  }}
                  className="bg-transparent border-b border-input rounded-none px-0 py-2 focus-visible:ring-0 focus-visible:border-primary transition-colors"
                  placeholder="e.g. Employee of the Year 2022, Sales Target Exceeded by 150%"
                />
              </div>
              <Button
                data-testid={`remove-achievement-${index}`}
                onClick={() => {
                  const updated = (cvData.achievements || []).filter((_, i) => i !== index);
                  setCvData({ ...cvData, achievements: updated });
                }}
                variant="ghost"
                size="sm"
                className="hover:bg-destructive/10 hover:text-destructive rounded-none mt-5"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
          {(!cvData.achievements || cvData.achievements.length === 0) && (
            <p className="text-sm text-muted-foreground italic">No achievements added yet. Click "Add" to highlight your accomplishments.</p>
          )}
        </div>
      </section>

      {/* Personal Attributes */}
      <section>
        <div className="flex justify-between items-center mb-6 pb-2 border-b border-border">
          <h2 className="font-heading text-xl font-medium">Personal Attributes (Optional)</h2>
          <Button
            data-testid="add-attribute-btn"
            onClick={() => setCvData({...cvData, attributes: [...(cvData.attributes || []), ""]})}
            variant="ghost"
            size="sm"
            className="hover:bg-accent/10 hover:text-accent rounded-none"
          >
            <Plus className="h-4 w-4 mr-1" />
            Add
          </Button>
        </div>
        <div className="space-y-4">
          {(cvData.attributes || []).map((attr, index) => (
            <div key={index} className="flex gap-4 items-start p-4 border border-border/40 rounded-none relative">
              <div className="flex-1">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Attribute</Label>
                <Input
                  data-testid={`attribute-${index}`}
                  value={attr}
                  onChange={(e) => {
                    const updated = [...(cvData.attributes || [])];
                    updated[index] = e.target.value;
                    setCvData({ ...cvData, attributes: updated });
                  }}
                  className="bg-transparent border-b border-input rounded-none px-0 py-2 focus-visible:ring-0 focus-visible:border-primary transition-colors"
                  placeholder="e.g. Strong leadership, Problem solver, Team player"
                />
              </div>
              <Button
                data-testid={`remove-attribute-${index}`}
                onClick={() => {
                  const updated = (cvData.attributes || []).filter((_, i) => i !== index);
                  setCvData({ ...cvData, attributes: updated });
                }}
                variant="ghost"
                size="sm"
                className="hover:bg-destructive/10 hover:text-destructive rounded-none mt-5"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
          {(!cvData.attributes || cvData.attributes.length === 0) && (
            <p className="text-sm text-muted-foreground italic">No attributes added yet. Click "Add" to describe your personal qualities.</p>
          )}
        </div>
      </section>

      {/* References */}
      <section>
        <div className="flex justify-between items-center mb-6 pb-2 border-b border-border">
          <h2 className="font-heading text-xl font-medium">References (Optional)</h2>
          <Button
            data-testid="add-reference-btn"
            onClick={() => setCvData({...cvData, references: [...(cvData.references || []), { name: "", title: "", company: "", phone: "", email: "" }]})}
            variant="ghost"
            size="sm"
            className="hover:bg-accent/10 hover:text-accent rounded-none"
          >
            <Plus className="h-4 w-4 mr-1" />
            Add
          </Button>
        </div>
        <div className="space-y-6">
          {(cvData.references || []).map((ref, index) => (
            <div key={index} className="space-y-4 p-4 border border-border/40 rounded-none relative">
              <Button
                data-testid={`remove-reference-${index}`}
                onClick={() => {
                  const updated = (cvData.references || []).filter((_, i) => i !== index);
                  setCvData({ ...cvData, references: updated });
                }}
                variant="ghost"
                size="sm"
                className="absolute top-2 right-2 hover:bg-destructive/10 hover:text-destructive rounded-none"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
              <div>
                <Label className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Full Name</Label>
                <Input
                  data-testid={`ref-name-${index}`}
                  value={ref.name}
                  onChange={(e) => {
                    const updated = [...(cvData.references || [])];
                    updated[index].name = e.target.value;
                    setCvData({ ...cvData, references: updated });
                  }}
                  className="bg-transparent border-b border-input rounded-none px-0 py-2 focus-visible:ring-0 focus-visible:border-primary transition-colors"
                  placeholder="Reference name"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Job Title</Label>
                  <Input
                    data-testid={`ref-title-${index}`}
                    value={ref.title}
                    onChange={(e) => {
                      const updated = [...(cvData.references || [])];
                      updated[index].title = e.target.value;
                      setCvData({ ...cvData, references: updated });
                    }}
                    className="bg-transparent border-b border-input rounded-none px-0 py-2 focus-visible:ring-0 focus-visible:border-primary transition-colors"
                    placeholder="Their job title"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Company</Label>
                  <Input
                    data-testid={`ref-company-${index}`}
                    value={ref.company}
                    onChange={(e) => {
                      const updated = [...(cvData.references || [])];
                      updated[index].company = e.target.value;
                      setCvData({ ...cvData, references: updated });
                    }}
                    className="bg-transparent border-b border-input rounded-none px-0 py-2 focus-visible:ring-0 focus-visible:border-primary transition-colors"
                    placeholder="Company name"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Phone</Label>
                  <Input
                    data-testid={`ref-phone-${index}`}
                    value={ref.phone}
                    onChange={(e) => {
                      const updated = [...(cvData.references || [])];
                      updated[index].phone = e.target.value;
                      setCvData({ ...cvData, references: updated });
                    }}
                    className="bg-transparent border-b border-input rounded-none px-0 py-2 focus-visible:ring-0 focus-visible:border-primary transition-colors"
                    placeholder="Contact number"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Email</Label>
                  <Input
                    data-testid={`ref-email-${index}`}
                    type="email"
                    value={ref.email}
                    onChange={(e) => {
                      const updated = [...(cvData.references || [])];
                      updated[index].email = e.target.value;
                      setCvData({ ...cvData, references: updated });
                    }}
                    className="bg-transparent border-b border-input rounded-none px-0 py-2 focus-visible:ring-0 focus-visible:border-primary transition-colors"
                    placeholder="Email address"
                  />
                </div>
              </div>
            </div>
          ))}
          {(!cvData.references || cvData.references.length === 0) && (
            <p className="text-sm text-muted-foreground italic">No references added yet. Click "Add" to include professional references.</p>
          )}
        </div>
      </section>
    </div>
  );
};

export default CVForm;
